import { Link } from 'react-router-dom';
import { IoBusinessOutline } from 'react-icons/io5';
import { useAuth } from '../contexts/AuthContext';
import { useMyChurchProgress } from '../hooks/useMyChurchProgress';
import LoadingSpinner from '../components/LoadingSpinner';
import ChurchChallengeSection from '../components/church/ChurchChallengeSection';
import './ChurchPage.css';

/**
 * Read-only church page for members (WS4 leftover).
 *
 * Shows this church's chapter map for the active challenge window —
 * not the community map. Church admins see the same section on
 * Church Management.
 */
const ChurchPage = () => {
  const { user } = useAuth();
  const {
    payload,
    loading,
    error,
    retry,
    status,
    challenge,
    church,
    stats
  } = useMyChurchProgress();

  const churchName = church?.name || user?.church?.name || 'your church';

  if (loading) {
    return (
      <div className="church-page church-page--loading">
        <LoadingSpinner size="large" message="Loading church progress..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="church-page church-page--error">
        <div className="church-page-message">
          <h2>Unable to load church progress</h2>
          <p>{error}</p>
          <button type="button" className="church-page-retry" onClick={retry}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (status === 'no-challenge') {
    return (
      <div className="church-page">
        <div className="church-page-empty">
          <IoBusinessOutline size={48} aria-hidden="true" />
          <h1>My Church</h1>
          <p>
            No reading challenge is running right now. When one starts,
            this page will show {churchName}&apos;s map for that season.
          </p>
          <Link to="/church-management" className="church-page-link">
            Manage church
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'no-church') {
    return (
      <div className="church-page">
        <div className="church-page-empty">
          <IoBusinessOutline size={48} aria-hidden="true" />
          <h1>My Church</h1>
          <p>
            Join a church and wait for approval to see your church&apos;s
            challenge map. If you administer a church, you can create or
            manage it here.
          </p>
          <Link to="/church-management" className="church-page-link">
            Manage church
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="church-page">
      <ChurchChallengeSection
        challenge={challenge}
        church={church}
        stats={stats}
        payload={payload}
      />
      <p className="church-page-admin-link">
        <Link to="/church-management">Manage church</Link>
      </p>
    </div>
  );
};

export default ChurchPage;
